/* ================================================================
   TypographyPanel — shared typography editing panel
   ================================================================
   Extracted from EditableText for reuse across all text editing
   components (EditableText, BlockFormatBar, SectionBlocks, etc.)

   Features:
   - Font size (preset + custom px)
   - Font weight (6 levels)
   - Inline formatting (Bold, Underline, Lists, Symbols)
   - Text color (32-color palette + native picker + hex input)
   - Live preview
   - Reset all
   ================================================================ */

import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Bold, Underline, List, ListOrdered, Minus, ChevronDown,
  X, RotateCcw, Type, Check,
} from "lucide-react";
import { execFormat, isFormatActive } from "./InlineFormatToolbar";

/* ================================================================
   Constants (exported for reuse)
   ================================================================ */

export const FONT_SIZES = ["10", "12", "13", "14", "16", "18", "20", "24", "28", "32", "36", "40", "48", "60", "72", "96"];

export const FONT_WEIGHTS: { value: string; label: string }[] = [
  { value: "300", label: "\u05E8\u05D6\u05D4" },
  { value: "400", label: "\u05E8\u05D2\u05D9\u05DC" },
  { value: "500", label: "\u05D1\u05D9\u05E0\u05D5\u05E0\u05D9" },
  { value: "600", label: "\u05D7\u05E6\u05D9-\u05E2\u05D1\u05D4" },
  { value: "700", label: "\u05DE\u05D5\u05D3\u05D2\u05E9" },
  { value: "900", label: "\u05E9\u05D7\u05D5\u05E8" },
];

export const COLOR_PALETTE: string[] = [
  "#000000", "#111827", "#374151", "#6b7280", "#9ca3af", "#d1d5db", "#f3f4f6", "#ffffff",
  "#1e3a5f", "#1a4494", "#1d4ed8", "#3b82f6", "#60a5fa", "#0891b2", "#06b6d4", "#a5f3fc",
  "#14532d", "#16a34a", "#22c55e", "#84cc16", "#ca8a04", "#d97706", "#f59e0b", "#fbbf24",
  "#991b1b", "#dc2626", "#f87171", "#be185d", "#ec4899", "#7c3aed", "#a855f7", "#c084fc",
];

export const BULLET_SYMBOLS = [
  { char: "\u2022", label: "\u05E0\u05E7\u05D5\u05D3\u05D4" },
  { char: "\u2013", label: "\u05DE\u05E7\u05E3" },
  { char: "\u2714", label: "\u05E1\u05D9\u05DE\u05DF \u05D5\u05D9" },
  { char: "\u2605", label: "\u05DB\u05D5\u05DB\u05D1" },
  { char: "\u25B8", label: "\u05D7\u05E5" },
  { char: "\u25CB", label: "\u05E2\u05D9\u05D2\u05D5\u05DC" },
];

/* ================================================================
   Helpers
   ================================================================ */

export function calcPanelPos(anchorEl: HTMLElement | null, panelW = 284): { top: number; left: number } {
  if (!anchorEl) return { top: 60, left: 20 };
  const rect = anchorEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const panelH = 530;

  let top = rect.bottom + 4;
  if (top + panelH > vh - 8) top = Math.max(8, rect.top - panelH - 4);

  let left = rect.left - Math.floor(panelW / 2);
  if (left < 8) left = 8;
  if (left + panelW > vw - 8) left = vw - panelW - 8;

  return { top, left };
}

/* ================================================================
   InlineFormatButtons
   ================================================================ */

export function InlineFormatButtons({ editableEl, showBullets = false }: { editableEl: HTMLElement | null; showBullets?: boolean }) {
  const [, forceUpdate] = useState(0);
  const [showSymbolMenu, setShowSymbolMenu] = useState(false);

  useEffect(() => {
    const handler = () => forceUpdate((n) => n + 1);
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, []);

  const formatButtons: { key: string; icon: typeof Bold; command: string; label: string; shortcut: string }[] = [
    { key: "bold", icon: Bold, command: "bold", label: "\u05D4\u05D3\u05D2\u05E9\u05D4", shortcut: "Ctrl+B" },
    { key: "underline", icon: Underline, command: "underline", label: "\u05E7\u05D5 \u05EA\u05D7\u05EA\u05D5\u05DF", shortcut: "Ctrl+U" },
  ];

  if (showBullets) {
    formatButtons.push(
      { key: "list", icon: List, command: "insertUnorderedList", label: "\u05E0\u05E7\u05D5\u05D3\u05D5\u05EA", shortcut: "" },
      { key: "ordered", icon: ListOrdered, command: "insertOrderedList", label: "\u05DE\u05E1\u05E4\u05D5\u05E8", shortcut: "" },
    );
  }

  const insertSymbol = (char: string) => {
    if (editableEl) editableEl.focus();
    execFormat("insertHTML", `${char}\u00A0`);
    setShowSymbolMenu(false);
    forceUpdate((n) => n + 1);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1">
        {formatButtons.map(({ key, icon: Icon, command, label, shortcut }) => {
          const active = isFormatActive(command);
          return (
            <button
              key={key}
              type="button"
              className={`btnReset flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs ${
                active
                  ? "text-white shadow-sm"
                  : "text-gray-600 hover:text-purple-700"
              }`}
              style={{
                fontWeight: active ? 700 : 500,
                backgroundColor: active ? "#7c3aed" : "#f3f4f6",
              }}
              title={`${label}${shortcut ? ` (${shortcut})` : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (editableEl) editableEl.focus();
                execFormat(command);
                forceUpdate((n) => n + 1);
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          );
        })}

        {/* Symbol insertion dropdown */}
        {showBullets && (
          <div className="relative">
            <button
              type="button"
              className={`btnReset flex items-center gap-0.5 px-2 py-1.5 rounded-lg text-xs ${
                showSymbolMenu
                  ? "text-white shadow-sm"
                  : "text-gray-600 hover:text-purple-700"
              }`}
              style={{
                fontWeight: showSymbolMenu ? 700 : 500,
                backgroundColor: showSymbolMenu ? "#7c3aed" : "#f3f4f6",
              }}
              title={"\u05D4\u05DB\u05E0\u05E1\u05EA \u05E1\u05DE\u05DC"}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowSymbolMenu(!showSymbolMenu);
              }}
            >
              <Minus className="w-3.5 h-3.5" />
              <ChevronDown className="w-2.5 h-2.5" />
            </button>
            {showSymbolMenu && (
              <>
                <div className="fixed inset-0 z-[1299]" onClick={() => setShowSymbolMenu(false)} />
                <div
                  className="absolute top-full right-0 mt-1 z-[1300] bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[130px]"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                  <p className="text-[9px] text-gray-400 px-3 py-1" style={{ fontWeight: 600 }}>
                    {"\u05D4\u05DB\u05E0\u05E1\u05EA \u05E1\u05DE\u05DC \u05D1\u05E9\u05D5\u05E8\u05D4"}
                  </p>
                  {BULLET_SYMBOLS.map(({ char, label }) => (
                    <button
                      key={char}
                      type="button"
                      className="btnReset w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 cursor-pointer"
                      onClick={() => insertSymbol(char)}
                    >
                      <span className="w-5 text-center text-base">{char}</span>
                      <span className="text-xs">{label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Keyboard hint */}
        <span className="text-[9px] text-gray-400 mr-auto" style={{ direction: "ltr" }}>
          Ctrl+B / U
        </span>
      </div>
    </div>
  );
}

/* ================================================================
   TypographyPanel — main panel component (portal)
   ================================================================ */

export interface TypographyPanelProps {
  savedFontSize: string;
  savedColor: string;
  savedFontWeight: string;
  onFontSize: (v: string) => void;
  onColor: (v: string) => void;
  onWeight: (v: string) => void;
  onReset: () => void;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  editableEl: HTMLElement | null;
  showBullets?: boolean;
}

export function TypographyPanel({
  savedFontSize, savedColor, savedFontWeight,
  onFontSize, onColor, onWeight, onReset, onClose, anchorEl,
  editableEl, showBullets = false,
}: TypographyPanelProps) {
  const [sizeInput, setSizeInput] = useState(savedFontSize ? savedFontSize.replace("px", "") : "");
  const [pickerColor, setPickerColor] = useState(savedColor || "#000000");
  const pos = calcPanelPos(anchorEl);

  useEffect(() => {
    setSizeInput(savedFontSize ? savedFontSize.replace("px", "") : "");
  }, [savedFontSize]);

  useEffect(() => {
    if (savedColor && /^#[0-9a-fA-F]{3,8}$/.test(savedColor)) setPickerColor(savedColor);
    else if (!savedColor) setPickerColor("#000000");
  }, [savedColor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleColorChange = useCallback((color: string) => {
    setPickerColor(color || "#000000");
    onColor(color);
  }, [onColor]);

  const isTypoActive = !!(savedFontSize || savedColor || savedFontWeight);

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[1299]" onClick={onClose} />

      {/* Panel */}
      <div
        className="fixed z-[1300] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
        style={{ top: pos.top, left: pos.left, width: 284 }}
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200" style={{ background: "linear-gradient(135deg, #f5f3ff 0%, #eff6ff 100%)" }}>
          <div className="flex items-center gap-1.5">
            <Type className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs text-gray-700" style={{ fontWeight: 700 }}>
              {"\u05E2\u05D9\u05E6\u05D5\u05D1 \u05D8\u05E7\u05E1\u05D8"}
            </span>
          </div>
          <button type="button" className="btnReset w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" onClick={onClose}>
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Live preview bar */}
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 text-center overflow-hidden">
          <span
            className="text-gray-700"
            style={{
              ...(savedFontSize ? { fontSize: savedFontSize } : { fontSize: "15px" }),
              ...(savedColor ? { color: savedColor } : {}),
              ...(savedFontWeight ? { fontWeight: Number(savedFontWeight) } : {}),
            }}
          >
            {"\u05D3\u05D5\u05D2\u05DE\u05D0 \u05DC\u05D8\u05E7\u05E1\u05D8"}
          </span>
        </div>

        <div className="p-3 space-y-3">
          {/* === Font Size === */}
          <div>
            <p className="text-[9px] uppercase tracking-widest text-gray-400 mb-1.5" style={{ fontWeight: 700 }}>
              {"\u05D2\u05D5\u05D3\u05DC \u05D2\u05D5\u05E4\u05DF"}
            </p>
            <div className="flex flex-wrap gap-1">
              {FONT_SIZES.map((size) => {
                const val = size + "px";
                const isActive = savedFontSize === val;
                return (
                  <button
                    key={size}
                    type="button"
                    className={`btnReset px-1.5 py-0.5 rounded text-[11px] font-mono ${
                      isActive
                        ? "bg-purple-500 text-white shadow-sm"
                        : "bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-700"
                    }`}
                    style={{ fontWeight: isActive ? 700 : 400 }}
                    onClick={() => {
                      const next = isActive ? "" : val;
                      onFontSize(next);
                      setSizeInput(isActive ? "" : size);
                    }}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-1 mt-1.5">
              <span className="text-[10px] text-gray-400 shrink-0">{"\u05DE\u05D5\u05EA\u05D0\u05DD"}:</span>
              <input
                type="number"
                min="6"
                max="400"
                value={sizeInput}
                placeholder="px"
                className="w-14 px-2 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-purple-400 font-mono text-left"
                onChange={(e) => setSizeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && sizeInput) onFontSize(sizeInput + "px");
                }}
              />
              <button
                type="button"
                className="btnReset px-1.5 py-0.5 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-30 flex items-center"
                disabled={!sizeInput}
                onClick={() => { if (sizeInput) onFontSize(sizeInput + "px"); }}
              >
                <Check className="w-3 h-3" />
              </button>
              {savedFontSize && (
                <button type="button" className="btnReset text-gray-400 hover:text-red-500 p-0.5 rounded" onClick={() => { onFontSize(""); setSizeInput(""); }}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* === Font Weight === */}
          <div>
            <p className="text-[9px] uppercase tracking-widest text-gray-400 mb-1.5" style={{ fontWeight: 700 }}>
              {"\u05E2\u05D5\u05D1\u05D9 \u05D2\u05D5\u05E4\u05DF"}
            </p>
            <div className="flex flex-wrap gap-1">
              {FONT_WEIGHTS.map((fw) => {
                const isActive = savedFontWeight === fw.value;
                return (
                  <button
                    key={fw.value}
                    type="button"
                    className={`btnReset px-2 py-0.5 rounded text-xs ${
                      isActive
                        ? "bg-purple-500 text-white shadow-sm"
                        : "bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-700"
                    }`}
                    style={{ fontWeight: Number(fw.value) }}
                    onClick={() => onWeight(isActive ? "" : fw.value)}
                  >
                    {fw.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* === Inline Formatting (B / U / Bullets) === */}
          <div>
            <p className="text-[9px] uppercase tracking-widest text-gray-400 mb-1.5" style={{ fontWeight: 700 }}>
              {"\u05E2\u05D9\u05E6\u05D5\u05D1 \u05D9\u05E0\u05DC\u05D9\u05D9\u05DF"}
            </p>
            <InlineFormatButtons editableEl={editableEl} showBullets={showBullets} />
          </div>

          {/* === Text Color === */}
          <div>
            <p className="text-[9px] uppercase tracking-widest text-gray-400 mb-1.5" style={{ fontWeight: 700 }}>
              {"\u05E6\u05D1\u05E2 \u05D8\u05E7\u05E1\u05D8"}
            </p>
            <div className="grid grid-cols-8 gap-1">
              {COLOR_PALETTE.map((color) => {
                const isActive = !!savedColor && savedColor.toLowerCase() === color.toLowerCase();
                return (
                  <button
                    key={color}
                    type="button"
                    className={`btnReset w-6 h-6 rounded-md ${
                      isActive ? "ring-2 ring-purple-500 ring-offset-1 scale-110" : "hover:scale-110"
                    } ${color === "#ffffff" ? "border border-gray-200" : ""}`}
                    style={{ backgroundColor: color }}
                    title={color}
                    onClick={() => handleColorChange(isActive ? "" : color)}
                  />
                );
              })}
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              <input
                type="color"
                value={pickerColor}
                className="w-7 h-7 rounded cursor-pointer border border-gray-200 p-0.5 shrink-0"
                title={"\u05D1\u05D7\u05E8 \u05E6\u05D1\u05E2 \u05DE\u05D5\u05EA\u05D0\u05DD"}
                onChange={(e) => handleColorChange(e.target.value)}
              />
              <input
                type="text"
                value={pickerColor}
                placeholder="#000000"
                className="flex-1 px-2 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-purple-400 font-mono text-left"
                onChange={(e) => {
                  setPickerColor(e.target.value);
                  if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) onColor(e.target.value);
                }}
              />
              {savedColor && (
                <button
                  type="button"
                  className="btnReset text-gray-400 hover:text-red-500 p-0.5 rounded"
                  title={"\u05D4\u05E1\u05E8 \u05E6\u05D1\u05E2"}
                  onClick={() => { onColor(""); setPickerColor("#000000"); }}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer: reset all */}
        <div className="px-3 pb-3">
          <button
            type="button"
            className="btnReset w-full py-1.5 text-xs text-gray-500 bg-gray-50 hover:bg-red-50 hover:text-red-500 rounded-lg border border-gray-100 hover:border-red-200 flex items-center justify-center gap-1.5 disabled:opacity-30"
            disabled={!isTypoActive}
            onClick={onReset}
          >
            <RotateCcw className="w-3 h-3" />
            {"\u05D0\u05E4\u05E1 \u05E2\u05D9\u05E6\u05D5\u05D1 \u05D8\u05E7\u05E1\u05D8"}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
